import { useMemo } from 'react'

import { useFetchAccounts } from '@features/accounts/hooks/useFetchAccounts'
import type { Contact } from '@features/contacts/types/contact'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useFetchPurchaseTaxRates } from '../../../../../hooks/useFetchPurchaseTaxRates'
import type { ReviewEntryLine } from '../../../../../types/reviewEntryLine'
import { getReviewEntryCurrencyId } from '../../../../../utils/getReviewEntryCurrencyId'
import { getReviewEntryLineDefaultValues } from '../../../../../utils/getReviewEntryLineDefaultValues'
import { parseReceiptDate } from '../../../../../utils/parseReceiptDate'

interface UseReviewEntryDataFormDefaultValuesProps {
  currencyId?: string
  entryLines: ReviewEntryLine[]
  invoiceNumber?: string
  receiptDate?: string
  supplierContact?: Partial<Contact>
  supplierId?: string
  supplierName?: string
}

// mirrors the bill form `vendor` field: the form holds a whole contact so `ContactSelect` can
// render the name directly. an AI-extracted name without a matching contact becomes `{ name }`
// (no id) — that is what triggers ContactSelect's inline "create new contact" affordance.
const getDefaultSupplier = ({
  supplierContact,
  supplierId,
  supplierName,
}: Pick<UseReviewEntryDataFormDefaultValuesProps, 'supplierContact' | 'supplierId' | 'supplierName'>):
  | Partial<Contact>
  | undefined => {
  if (supplierContact?.id && supplierContact.id === supplierId) {
    return supplierContact
  }

  // id resolved upstream but the contact fetch failed transiently — keep the id valid for
  // submit and fall back to the extracted name for display
  if (supplierId) {
    return { id: supplierId, name: supplierName || undefined }
  }

  return supplierName ? { name: supplierName } : undefined
}

export const useReviewEntryDataFormDefaultValues = ({
  currencyId,
  entryLines,
  invoiceNumber,
  receiptDate,
  supplierContact,
  supplierId,
  supplierName,
}: UseReviewEntryDataFormDefaultValuesProps) => {
  const { organization } = useUserOrganization()
  const { purchaseTaxRates, isLoading: isPurchaseTaxRatesLoading } = useFetchPurchaseTaxRates()
  const { accounts, isIdle: isAccountsIdle, isLoading: isAccountsLoading } = useFetchAccounts()

  const isDataPending = isPurchaseTaxRatesLoading || isAccountsIdle || isAccountsLoading

  const defaultValues = useMemo(
    () => ({
      currencyId: getReviewEntryCurrencyId(currencyId, organization?.baseCurrencyId),
      entryLines: entryLines.map((line) => getReviewEntryLineDefaultValues({ accounts, line, purchaseTaxRates })),
      invoiceNumber: invoiceNumber ?? '',
      receiptDate: parseReceiptDate(receiptDate),
      supplier: getDefaultSupplier({ supplierContact, supplierId, supplierName }),
    }),
    [
      accounts,
      currencyId,
      entryLines,
      invoiceNumber,
      organization?.baseCurrencyId,
      purchaseTaxRates,
      receiptDate,
      supplierContact,
      supplierId,
      supplierName,
    ],
  )

  return { accounts, defaultValues, isDataPending }
}
